import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../lib/supabase'
import { Deal, User } from '../../types'
import {
  FreightOutWorkspace,
  FreightStatus,
  PickupWorkspace,
  SaleWorkspace,
  ShippingParty,
  checkFinalSale,
  confirmPickup,
  createFinalSale,
  loadFreightCarrierNames,
  loadFreightOut,
  loadPickup,
  openSaleWorkspace,
  recordPickupFailure,
  saleDocumentUrl,
  saveFreightOut,
  saveSaleWorkspace,
} from '../../services/saleWorkspaceService'
import { toast } from '../Toast'
import { AmendmentPanel, CorrectionPanel } from './SaleGovernancePanels'
import './SaleDetailsView.css'

/**
 * Sale Details (Workspace) — the outflow mirror of Purchase Details (Workspace).
 *
 * A deal sitting in `deal_pending` has a sales proposal against it and, until now, nowhere to
 * finish the sale. This is that place: confirm who is buying, decide who ships, and press
 * Create Final Sale once.
 *
 * The screen deliberately holds no opinion about what makes a sale valid. Every rule lives in
 * `saleWorkspaceService` — the gate shown here is the same function the button calls, so the
 * list of reasons a person is looking at cannot drift away from the reasons the action will
 * actually refuse for. A screen that computes its own version of a rule is how two parts of
 * this platform end up disagreeing about the same deal.
 */

const money = (n?: number | null) =>
  n == null || !Number.isFinite(Number(n))
    ? '—'
    : '$' + Number(n).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const shortDate = (iso?: string | null) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const vehicleOf = (d: Deal) =>
  [d.autograb_year ?? d.vehicle_year, d.autograb_make || d.vehicle_make, d.autograb_model || d.vehicle_model]
    .filter(Boolean)
    .join(' ') || 'Vehicle not recorded'

const vinOf = (d: Deal) => d.vehicle_vin || d.autograb_vin || ''

export default function SaleDetailsView({ user }: { user: User }) {
  const [deals, setDeals] = useState<Deal[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [sortBy, setSortBy] = useState<'date' | 'buyer' | 'price' | 'vin'>('date')
  const [showFinalised, setShowFinalised] = useState(false)
  const [finalisedIds, setFinalisedIds] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState<Deal | null>(null)

  useEffect(() => {
    void load()
  }, [])

  const load = async () => {
    setLoading(true)
    try {
      /* Everything that has reached a sale, so the screen can show what it has finished as
         well as what it has left to do. A person who has just pressed the button wants to
         see the result rather than watch the card vanish. */
      const { data, error } = await supabase
        .from('deals')
        .select('*')
        .in('deal_status', ['deal_pending', 'sold_in_transit', 'sold_delivered'])
        .is('deleted_at', null)
        .order('updated_at', { ascending: false })
      if (error) throw error
      setDeals((data || []) as Deal[])

      const { data: ws } = await supabase
        .from('sale_workspaces')
        .select('deal_id, finalised_at')
        .not('finalised_at', 'is', null)
      setFinalisedIds(new Set(((ws || []) as { deal_id: string }[]).map((r) => r.deal_id)))
    } catch (err) {
      console.error('Could not load the sale list', err)
    } finally {
      setLoading(false)
    }
  }

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase()
    let out = deals.filter((d) => (showFinalised ? true : !finalisedIds.has(d.id)))
    if (term) {
      out = out.filter((d) =>
        [d.buyer_name, d.buyer_company, vehicleOf(d), vinOf(d), d.stock_number]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term)),
      )
    }
    return [...out].sort((a, b) => {
      switch (sortBy) {
        case 'buyer':
          return String(a.buyer_name || a.buyer_company || '').localeCompare(String(b.buyer_name || b.buyer_company || ''))
        case 'price':
          return Number(b.sale_price || 0) - Number(a.sale_price || 0)
        case 'vin':
          return vinOf(a).localeCompare(vinOf(b))
        default:
          return new Date(b.updated_at || b.created_at).getTime() - new Date(a.updated_at || a.created_at).getTime()
      }
    })
  }, [deals, search, sortBy, showFinalised, finalisedIds])

  const pending = shown.filter((d) => d.deal_status === 'deal_pending')
  const sold = shown.filter((d) => d.deal_status !== 'deal_pending')

  const card = (deal: Deal) => (
    <div key={deal.id} className="sd-card">
      <div className="sd-card-top">
        <div>
          <h4 className="sd-vehicle">{vehicleOf(deal)}</h4>
          <p className="sd-buyer">{deal.buyer_name || deal.buyer_company || 'No buyer recorded'}</p>
        </div>
        <p className="sd-price">{money(deal.sale_price)}</p>
      </div>
      <div className="sd-card-meta">
        <span className="sd-badge">VIN {vinOf(deal) ? vinOf(deal).slice(0, 10) + '…' : '—'}</span>
        <span className="sd-badge">Stock {deal.stock_number || '—'}</span>
        {finalisedIds.has(deal.id) && <span className="sd-pill sd-pill-ok">Finalised</span>}
      </div>
      <div className="sd-card-actions">
        <button type="button" className="sd-btn-primary" onClick={() => setOpen(deal)}>
          {finalisedIds.has(deal.id) ? 'Open sale' : 'Open sale workspace'}
        </button>
      </div>
    </div>
  )

  return (
    <div className="sd-view">
      <div className="sd-header">
        <h2>Sale Details (Workspace)</h2>
        <div className="sd-controls">
          <input
            type="text"
            className="sd-input"
            placeholder="Search by buyer, vehicle, VIN or stock"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select className="sd-select" value={sortBy} onChange={(e) => setSortBy(e.target.value as never)}>
            <option value="date">Sort by last change</option>
            <option value="buyer">Sort by buyer</option>
            <option value="price">Sort by sale price</option>
            <option value="vin">Sort by VIN</option>
          </select>
          <label className="sd-check">
            <input type="checkbox" checked={showFinalised} onChange={(e) => setShowFinalised(e.target.checked)} />
            Show sales already finalised
          </label>
        </div>
      </div>

      {loading ? (
        <p className="sd-empty">Loading sales…</p>
      ) : (
        <div className="sd-columns">
          <section className="sd-column">
            <h3 className="sd-column-title">Awaiting final sale ({pending.length})</h3>
            {pending.length === 0 ? (
              <p className="sd-empty">
                Nothing is waiting. A deal appears here once it reaches <strong>Deal Pending</strong> in the pipeline.
              </p>
            ) : (
              <div className="sd-list">{pending.map(card)}</div>
            )}
          </section>
          <section className="sd-column">
            <h3 className="sd-column-title">Sold ({sold.length})</h3>
            {sold.length === 0 ? (
              <p className="sd-empty">No sold vehicles in this filter.</p>
            ) : (
              <div className="sd-list">{sold.map(card)}</div>
            )}
          </section>
        </div>
      )}

      {open && (
        <SaleWorkspacePanel
          deal={open}
          user={user}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null)
            void load()
          }}
        />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------------------------------
 * The workspace itself
 * ---------------------------------------------------------------------------------------- */

function SaleWorkspacePanel({
  deal,
  user,
  onClose,
  onDone,
}: {
  deal: Deal
  user: User
  onClose: () => void
  onDone: () => void
}) {
  const [ws, setWs] = useState<SaleWorkspace | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [working, setWorking] = useState(false)
  const [outcome, setOutcome] = useState<{ ok: boolean; message: string } | null>(null)
  const [links, setLinks] = useState<{ contract?: string | null; invoice?: string | null }>({})

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const found = await openSaleWorkspace(deal.id, user?.id || null)
        if (alive) setWs(found)
      } catch (err) {
        if (alive) setOutcome({ ok: false, message: 'This sale workspace could not be opened: ' + (err as Error).message })
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [deal.id, user?.id])

  /* The same gate the button calls. Not a second copy of the rules. */
  const gate = ws
    ? checkFinalSale({
        buyerAbn: ws.buyerAbn,
        buyerName: ws.buyerName,
        shippingParty: ws.shippingParty,
        salePrice: deal.sale_price,
        alreadyFinalised: Boolean(ws.finalisedAt),
        isVoided: ws.isVoided,
      })
    : { ok: false, problems: [] }

  const set = (patch: Partial<SaleWorkspace>) => setWs((prev) => (prev ? { ...prev, ...patch } : prev))

  const save = async () => {
    if (!ws) return
    setSaving(true)
    setOutcome(null)
    try {
      await saveSaleWorkspace(deal.id, {
        buyerName: ws.buyerName,
        buyerAbn: ws.buyerAbn,
        buyerLmct: ws.buyerLmct,
        buyerAddress: ws.buyerAddress,
        buyerEmail: ws.buyerEmail,
        buyerMobile: ws.buyerMobile,
        shippingParty: ws.shippingParty,
      })
      toast.saved()
      setOutcome({ ok: true, message: 'Saved.' })
    } catch (err) {
      setOutcome({ ok: false, message: 'Could not save: ' + (err as Error).message })
    } finally {
      setSaving(false)
    }
  }

  const finalise = async () => {
    if (!ws) return
    setWorking(true)
    setOutcome(null)
    try {
      /* Save first. Otherwise a person who typed an ABN and pressed the button straight away
         would have it validated against what the database still holds, which is the old one. */
      await saveSaleWorkspace(deal.id, {
        buyerName: ws.buyerName,
        buyerAbn: ws.buyerAbn,
        buyerLmct: ws.buyerLmct,
        buyerAddress: ws.buyerAddress,
        buyerEmail: ws.buyerEmail,
        buyerMobile: ws.buyerMobile,
        shippingParty: ws.shippingParty,
      })
      const result = await createFinalSale({ dealId: deal.id, user })
      setOutcome({ ok: result.ok, message: result.message })
      if (result.ok) {
        const fresh = await openSaleWorkspace(deal.id, user?.id || null)
        setWs(fresh)
      }
    } catch (err) {
      setOutcome({ ok: false, message: 'Create Final Sale failed: ' + (err as Error).message })
    } finally {
      setWorking(false)
    }
  }

  const openDoc = async (path: string | null, which: 'contract' | 'invoice') => {
    const url = await saleDocumentUrl(path)
    setLinks((prev) => ({ ...prev, [which]: url }))
    if (url) window.open(url, '_blank', 'noopener')
    else setOutcome({ ok: false, message: 'That document could not be opened. It may not have been filed.' })
  }

  const locked = Boolean(ws?.finalisedAt)

  return (
    <div className="sd-overlay" role="dialog" aria-modal="true">
      <div className="sd-panel">
        <div className="sd-panel-head">
          <div>
            <h3>{vehicleOf(deal)}</h3>
            <p className="sd-panel-sub">
              {money(deal.sale_price)} · VIN {vinOf(deal) || '—'} · Stock {deal.stock_number || '—'}
            </p>
          </div>
          <button type="button" className="sd-btn-quiet" onClick={onClose}>
            Close
          </button>
        </div>

        {loading ? (
          <p className="sd-empty">Opening…</p>
        ) : !ws ? (
          <p className="sd-empty">This sale workspace could not be opened.</p>
        ) : (
          <div className="sd-panel-body">
            <section className="sd-section">
              <h4 className="sd-section-title">Buyer</h4>
              <div className="sd-grid">
                <label className="sd-field">
                  <span>Name</span>
                  <input
                    className="sd-input"
                    value={ws.buyerName}
                    disabled={locked}
                    onChange={(e) => set({ buyerName: e.target.value })}
                  />
                </label>
                <label className="sd-field">
                  <span>ABN</span>
                  <input
                    className="sd-input"
                    value={ws.buyerAbn}
                    disabled={locked}
                    placeholder="11 digits"
                    onChange={(e) => set({ buyerAbn: e.target.value })}
                  />
                </label>
                <label className="sd-field">
                  <span>Licence (LMCT/MDL)</span>
                  <input
                    className="sd-input"
                    value={ws.buyerLmct}
                    disabled={locked}
                    onChange={(e) => set({ buyerLmct: e.target.value })}
                  />
                </label>
                <label className="sd-field sd-field-wide">
                  <span>Address</span>
                  <input
                    className="sd-input"
                    value={ws.buyerAddress}
                    disabled={locked}
                    onChange={(e) => set({ buyerAddress: e.target.value })}
                  />
                </label>
                <label className="sd-field">
                  <span>Email</span>
                  <input
                    className="sd-input"
                    value={ws.buyerEmail}
                    disabled={locked}
                    onChange={(e) => set({ buyerEmail: e.target.value })}
                  />
                </label>
                <label className="sd-field">
                  <span>Mobile</span>
                  <input
                    className="sd-input"
                    value={ws.buyerMobile}
                    disabled={locked}
                    onChange={(e) => set({ buyerMobile: e.target.value })}
                  />
                </label>
              </div>
            </section>

            <section className="sd-section">
              <h4 className="sd-section-title">Who ships</h4>
              <p className="sd-note">
                This decides where the vehicle lands: Direct Auto shipping puts it on <strong>Sold — In Transit</strong>,
                the buyer collecting puts it on <strong>Sold — Delivered</strong>.
              </p>
              <div className="sd-choices">
                {(
                  [
                    ['DirectAutoShips', 'Direct Auto ships it'],
                    ['BuyerShips', 'Buyer collects or arranges transport'],
                  ] as [ShippingParty, string][]
                ).map(([id, label]) => (
                  <label key={id} className={'sd-choice' + (ws.shippingParty === id ? ' sd-choice-on' : '')}>
                    <input
                      type="radio"
                      name="shipping"
                      checked={ws.shippingParty === id}
                      disabled={locked}
                      onChange={() => set({ shippingParty: id })}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </section>

            {!locked && (
              <section className="sd-section">
                <h4 className="sd-section-title">Before this sale can be created</h4>
                {gate.ok ? (
                  <p className="sd-ready">Everything this sale needs is here.</p>
                ) : (
                  <ul className="sd-problems">
                    {gate.problems.map((p) => (
                      <li key={p}>{p}</li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {locked && (
              <section className="sd-section">
                <h4 className="sd-section-title">Issued</h4>
                <div className="sd-grid">
                  <div className="sd-read">
                    <span>Deal number</span>
                    <strong>{ws.dealNumber || '—'}</strong>
                  </div>
                  <div className="sd-read">
                    <span>Finalised</span>
                    <strong>{shortDate(ws.finalisedAt)}</strong>
                  </div>
                  <div className="sd-read">
                    <span>Cooling-off ends</span>
                    <strong>{shortDate(ws.coolingOffExpiresAt)}</strong>
                  </div>
                </div>
                <div className="sd-doc-row">
                  <button type="button" className="sd-btn-secondary" onClick={() => openDoc(ws.contractPath, 'contract')}>
                    Open contract for sale
                  </button>
                  <button
                    type="button"
                    className="sd-btn-secondary"
                    onClick={() => openDoc(ws.taxInvoicePath, 'invoice')}
                  >
                    Open tax invoice
                  </button>
                </div>
                {(links.contract === null || links.invoice === null) && (
                  <p className="sd-note">A document that will not open has not been filed. Raise it rather than re-issuing.</p>
                )}
              </section>
            )}

            {locked && <MovementCard deal={deal} ws={ws} user={user} />}

            {/* Winnie, 18/20 Sep. Both panels only make sense once the sale is finalised:
                before that there is nothing to correct and nothing to amend — the workspace
                above is still editable in the ordinary way. */}
            {locked && (
              <>
                <CorrectionPanel deal={deal} user={user} />
                <AmendmentPanel
                  deal={deal}
                  coolingOffExpiresAt={ws.coolingOffExpiresAt}
                  isVoided={ws.isVoided}
                  user={user}
                  onChanged={() => {
                    void openSaleWorkspace(deal.id, user?.id || null).then(setWs)
                  }}
                />
              </>
            )}

            {outcome && (
              <div className={'sd-outcome ' + (outcome.ok ? 'sd-outcome-ok' : 'sd-outcome-bad')}>{outcome.message}</div>
            )}

            <div className="sd-panel-actions">
              <button type="button" className="sd-btn-quiet" onClick={onClose}>
                Close
              </button>
              {!locked && (
                <>
                  <button type="button" className="sd-btn-secondary" onClick={save} disabled={saving || working}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    className="sd-btn-primary"
                    onClick={finalise}
                    disabled={!gate.ok || working || saving}
                    title={gate.ok ? 'Issue the contract and tax invoice and move the vehicle' : gate.problems.join(' ')}
                  >
                    {working ? 'Creating…' : 'Create Final Sale'}
                  </button>
                </>
              )}
              {locked && (
                <button type="button" className="sd-btn-primary" onClick={onDone}>
                  Done
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Where the car has got to.
 *
 * Sale Details spec, section 10: the outflow mirror of Book Inspection. Which card shows
 * depends on who is moving the vehicle - a freight job has quotes, a carrier and a transit
 * to track; a buyer collecting has one fact, which is whether they turned up.
 *
 * It appears only once the sale is finalised, because before that there is nothing to move.
 * What these cards feed today, stated honestly: the transit-days figure on Sales Performance,
 * and a record of where the car is. They do NOT yet gate `settled` — the only settle gate in the
 * platform is full payment (dealStatusService's closed transition), and the PPSR discharge and
 * delivery gates the Sale Details spec section 6 and section 10 ask for are not built. Audited
 * and confirmed on 14 Sep; the earlier version of this comment claimed a gate that did not exist.
 */
function MovementCard({ deal, ws, user }: { deal: Deal; ws: SaleWorkspace; user: User }) {
  const [freight, setFreight] = useState<FreightOutWorkspace | null>(null)
  const [pickup, setPickup] = useState<PickupWorkspace | null>(null)
  const [collector, setCollector] = useState('')
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  /* Winnie, 15 Sep: the carrier list comes from the Admin transport panel, filtered to the
     carriers whose coverage includes freight out. It used to be a two-name constant in this
     module, one of which was not on the panel at all. */
  const [carriers, setCarriers] = useState<string[]>([])
  const ships = ws.shippingParty === 'DirectAutoShips'

  useEffect(() => {
    let alive = true
    if (!ships) return
    loadFreightCarrierNames('FreightOut')
      .then((names) => {
        if (alive) setCarriers(names)
      })
      /* A panel that cannot be read leaves the field as free text, which is what it already
         is — it must not stop the freight job being updated. */
      .catch(() => undefined)
    return () => {
      alive = false
    }
  }, [ships])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        if (ships) {
          const f = await loadFreightOut(deal.id)
          if (alive) setFreight(f)
        } else {
          const p = await loadPickup(deal.id)
          if (alive) {
            setPickup(p)
            setCollector(p?.confirmedBy || '')
          }
        }
      } catch (err) {
        if (alive) setNote('That could not be read: ' + (err as Error).message)
      }
    })()
    return () => {
      alive = false
    }
  }, [deal.id, ships])

  const patchFreight = async (patch: Parameters<typeof saveFreightOut>[1]) => {
    setBusy(true)
    setNote(null)
    try {
      setFreight(await saveFreightOut(deal.id, patch, user?.id || null))
      toast.saved()
    } catch (err) {
      setNote('That could not be saved: ' + (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  /* recordPickupFailure existed and was exported but had no caller — found in the 14 Sep audit.
     A no-show that is never written down cannot be reported on. */
  const failPickup = async (reason: string) => {
    if (!reason.trim()) return
    setBusy(true)
    setNote(null)
    try {
      await recordPickupFailure(deal.id, reason.trim())
      toast.saved()
      setPickup(await loadPickup(deal.id))
    } catch (err) {
      setNote('That could not be saved: ' + (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const confirm = async () => {
    if (!collector.trim()) {
      setNote('Record who collected the vehicle — it is often not the person named on the invoice.')
      return
    }
    setBusy(true)
    setNote(null)
    try {
      setPickup(await confirmPickup(deal.id, collector.trim(), user?.id || null))
      toast.saved()
    } catch (err) {
      setNote('That could not be saved: ' + (err as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const STATUSES: FreightStatus[] = ['NotStarted', 'QuotesRequested', 'Booked', 'InTransit', 'Delivered', 'Cancelled']
  const STATUS_LABEL: Record<FreightStatus, string> = {
    NotStarted: 'Not started',
    QuotesRequested: 'Quotes requested',
    Booked: 'Booked',
    InTransit: 'In transit',
    Delivered: 'Delivered',
    Cancelled: 'Cancelled',
  }

  return (
    <section className="sd-section">
      <h4 className="sd-section-title">{ships ? 'Freight out' : 'Pickup'}</h4>
      {ships ? (
        <>
          <div className="sd-grid">
            <label className="sd-field">
              <span>Where it stands</span>
              <select
                className="sd-input"
                value={freight?.status || 'NotStarted'}
                disabled={busy}
                onChange={(e) => void patchFreight({ status: e.target.value as FreightStatus })}
              >
                {STATUSES.map((st) => (
                  <option key={st} value={st}>{STATUS_LABEL[st]}</option>
                ))}
              </select>
            </label>
            <label className="sd-field">
              <span>Carrier</span>
              <input
                className="sd-input"
                list="sd-carriers"
                value={freight?.carrier || ''}
                disabled={busy}
                placeholder={carriers[0] || 'Carrier name'}
                onChange={(e) => setFreight(freight ? { ...freight, carrier: e.target.value } : freight)}
                onBlur={(e) => void patchFreight({ carrier: e.target.value })}
              />
              <datalist id="sd-carriers">
                {carriers.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </label>
            <label className="sd-field sd-field-wide">
              <span>Detail for the sales team</span>
              <input
                className="sd-input"
                value={freight?.whereItStands || ''}
                disabled={busy}
                placeholder="Booked — collecting Thursday"
                onChange={(e) => setFreight(freight ? { ...freight, whereItStands: e.target.value } : freight)}
                onBlur={(e) => void patchFreight({ whereItStands: e.target.value })}
              />
            </label>
          </div>
          <div className="sd-grid">
            <div className="sd-read">
              <span>Quotes requested</span>
              <strong>{shortDate(freight?.quoteRequestedAt || null)}</strong>
            </div>
            <div className="sd-read">
              <span>Booked</span>
              <strong>{shortDate(freight?.bookedAt || null)}</strong>
            </div>
            <div className="sd-read">
              <span>Delivered</span>
              <strong>{shortDate(freight?.deliveredAt || null)}</strong>
            </div>
          </div>
          {/* Winnie's spec section 10: a cancellation caused by a freight delay is recorded as
              its own reason, so shipping-caused cancellations can be counted apart from price
              renegotiations and no-shows. Shown only when it applies, so the form does not ask
              for a reason on a job that is going fine. */}
          {freight?.status === 'Cancelled' && (
            <label className="sd-field sd-field-wide">
              <span>Why it was cancelled</span>
              <input
                className="sd-input"
                value={freight?.cancelledReason || ''}
                disabled={busy}
                placeholder="Carrier could not collect before the buyer needed it"
                onChange={(e) => setFreight(freight ? { ...freight, cancelledReason: e.target.value } : freight)}
                onBlur={(e) => void patchFreight({ cancelledReason: e.target.value })}
              />
            </label>
          )}
          {!freight?.deliveredAt && freight?.status !== 'Cancelled' && (
            <p className="sd-note">
              Sales Performance measures transit from this job opening to Delivered, so marking it
              Delivered is what makes this vehicle count in that figure.
            </p>
          )}
        </>
      ) : (
        <>
          <div className="sd-grid">
            <label className="sd-field">
              <span>Who collected it</span>
              <input
                className="sd-input"
                value={collector}
                disabled={busy || Boolean(pickup?.pickupConfirmedAt)}
                placeholder="Name of whoever took the vehicle"
                onChange={(e) => setCollector(e.target.value)}
              />
            </label>
            <div className="sd-read">
              <span>Collected</span>
              <strong>{shortDate(pickup?.pickupConfirmedAt || null)}</strong>
            </div>
          </div>
          {!pickup?.pickupConfirmedAt && (
            <>
              <div className="sd-doc-row">
                <button type="button" className="sd-btn-secondary" onClick={confirm} disabled={busy}>
                  {busy ? 'Saving…' : 'Confirm pickup'}
                </button>
                <p className="sd-note">
                  The person who collects is often not the buyer contact named on the invoice, so it is
                  recorded by name.
                </p>
              </div>
              {/* A pickup that falls through is a different fact from a freight delay, and section 10
                  asks for them to be countable separately. Recording it does not cancel the sale —
                  that decision is section 9's, and is made on the deal. */}
              <label className="sd-field sd-field-wide">
                <span>Or: the buyer never collected it</span>
                <input
                  className="sd-input"
                  value={pickup?.cancelledReason || ''}
                  disabled={busy}
                  placeholder="Reason the pickup fell through"
                  onChange={(e) => setPickup(pickup ? { ...pickup, cancelledReason: e.target.value } : pickup)}
                  onBlur={(e) => void failPickup(e.target.value)}
                />
              </label>
            </>
          )}
        </>
      )}
      {note && <p className="sd-note">{note}</p>}
    </section>
  )
}
